import { AccordionDetailsChild, AccordionHeader, SocChildAccordion } from "./SOC.style";
import { AccordionSummary, Box, Button, Divider, TextField, Typography } from "@mui/material";
import { CalendarIcon, InfoIcon, SocCompleteIcon, SocStatusCustomIcon } from "../../../components/Icons";
import { IFullfilledMonthsResponse, IPetItemResponse, ISOCItemResponse }
  from "../../../services/Standard-of-care/standard-of-care.interface";
import React, { useEffect, useState } from "react";
import { colorTheme, socStatusMapper } from "../../../utils/SOCUtils";
import { format, isValid } from "date-fns";
import { formatDateFromUnixTimestamp, getUnixTimestamp } from "../../../utils/unix-timestamp";

import { CentralLoadingIndicator } from "../../../components";
import { ChipCustom } from "../../../styles/styledComponents";
import { CustomToolTip } from "../EditHospitalContainer/EditHospitalContainer.style";
import { DatePicker } from "@mui/x-date-pickers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { CREATE_BOOKING_TYPE, MODALS } from "../../../config/params";
import PreventativeMonthCarousal from "../../../components/PreventativeMonthCarousal/PreventativeMonthCarousal";
import SocCustomAvatar from "./SocCustomAvatar";
import { getSOCStatusDataMap } from "../../../utils/SOCUtils";
import { isValidDate as isValidDateUtils } from "../../../utils/date";
import { useAppointmentStore } from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useCreateSelfSoc } from "../../../services/self-soc/self-soc.service";
import { useModal } from "../../../context/ModalContext";
import { usePetCareStore } from "../../../hooks/redux-hooks/pet-care-data/pet-care-data-store";
import { useUserStore } from "../../../hooks/redux-hooks/user-store/user.store";
import { userActivityLogger } from "../../../utils/mixpanel";
import { useOverdueInvoiceStore } from "../../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { useBookingAppointmentTypeStore } from "../../../hooks";

type ISOCChildProps = {
  item: ISOCItemResponse;
  expandedChild: string | false;
  handleChangeChild: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  dynamicStyles?: boolean;
  isBooking?: boolean;
  petData: IPetItemResponse;
  isPreventative?: boolean;
  isWellnessPlan: boolean;
  onClick: (item: ISOCItemResponse) => void;
  specificOverdueItem?: string | null;
  isDashboard?: boolean;
  petID?: string;
}

const isValidSOCDate = (date: Date, selectedMonth: IFullfilledMonthsResponse) => {
  const dateMonth = date.getMonth() + 1;
  const dateYear = date.getFullYear();
  const selectedMonthNum = selectedMonth.monthIndex;
  const selectedYearNum = parseInt(selectedMonth.year);

  return (
    dateMonth === selectedMonthNum &&
    dateYear === selectedYearNum &&
    date <= new Date()
  );
};

const SOCChildAccordion: React.FC<ISOCChildProps> = (props: ISOCChildProps) => {
  const {
    item,
    expandedChild,
    handleChangeChild,
    dynamicStyles,
    petData,
    isPreventative,
    isWellnessPlan,
    onClick,
    isBooking = true,
    specificOverdueItem,
    petID,
    isDashboard = false,
  } = props;

  const [showMore, setShowMore] = useState(false);
  const [calendarDate, setCalendarDate] = useState<Date | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<IFullfilledMonthsResponse | null>(null);
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [isValidDate, setIsValidDate] = useState(true);
  const { pendingPaymentPetList, isBookingBlocked, pausedWellnessPetList, cancelledWellnessPetList } =
useOverdueInvoiceStore();


  const { addSelfSoc, loading, selfSocData, error, reset } = useCreateSelfSoc();
  const { updateFulfilledMonthStatus } = usePetCareStore();
  const { user } = useUserStore();
  const { setSelectedPets } = useAppointmentStore();
  const { openModal } = useModal();
  const { bookingAppointmentType } = useBookingAppointmentTypeStore();

  const handleShowMore = () => setShowMore(!showMore);

  const panelId = item.name + "_" + item.id + "_" + petID;

  const handleSocDataSelect = (selectedMonth: IFullfilledMonthsResponse | null) => {
    setSelectedMonth(selectedMonth);
  };
  const handleSave = () => {
    if (selectedMonth && calendarDate && !loading) {
      userActivityLogger(MIXPANEL_EVENTS.VISIT_PREVENTIVE_SAVE_CLICK, {
        "pet_name": petData.name,
        "wellness_subscriber": isWellnessPlan ? "yes" : "no",
        "preventative / medication_type": item.name,
        "last_fullfilled": item.lastGivenIso,
        "next_due": item.nextDoseIso,
        "fullfilled_date": calendarDate.toISOString(),
      });
      addSelfSoc({
        animalId: petData.id,
        eventName: item.name,
        monthIndex: selectedMonth.monthIndex,
        year: parseInt(selectedMonth.year),
        fulfilledDate: getUnixTimestamp(calendarDate),
      });
    }
  };

  const handleDateSelected = (date: Date | null) => {
    if (!date) {
      setCalendarDate(null);
      setIsValidDate(false);
      return;
    }

    if (!isValid(date)) {
      setIsValidDate(false);
      return;
    }

    if (!selectedMonth) {
      setIsValidDate(false);
      return;
    }

    const formattedDate = format(date, "MM/dd/yyyy");
    const isDateFormatValid = isValidDateUtils(formattedDate);

    if (!isDateFormatValid || !isValidSOCDate(date, selectedMonth)) {
      setIsValidDate(false);
      setCalendarDate(date);
    } else {
      setCalendarDate(date);
      setIsValidDate(true);
    }
  };


  const handleBookAppointment = () => {
    if (petData) {
      userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT, {
        start_from: "care_section",
        appt_type: bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ? "drop_off" : "in_room_visit",
        overdue_status: pendingPaymentPetList.length > 0 ? "yes" : "no",
        is_plan_cancelled: cancelledWellnessPetList.length > 0 ? "true" : "false",
      });
      setSelectedPets([petData]);
      if (!isBookingBlocked && (pendingPaymentPetList.length > 0)) {
        if (cancelledWellnessPetList.length > 0) {
          openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
        } else if (pausedWellnessPetList.length > 0) {
          openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL);
        } else {
          openModal(MODALS.GENERIC_PAYMENT_ISSUE_MODAL);
        }
      } else {
        openModal(MODALS.UPDATED_BOOK_A_VISIT_MODAL);
      }
    }
  };

  useEffect(() => {
    if (selectedMonth) {
      const startOfMonth = new Date(Number(selectedMonth.year), selectedMonth.monthIndex - 1, 1);

      setMinDate(startOfMonth);
      setCalendarDate(startOfMonth);
      setIsValidDate(true);
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (selfSocData) {
      userActivityLogger(MIXPANEL_EVENTS.ADD_SELF_REPORT_SUCCESS, {
        "pet_name": petData.name,
        "wellness_subscriber": isWellnessPlan ? "yes" : "no",
        "preventative / medication_type": item.name,
        "last_fullfilled": item.lastGivenIso,
        "next_due": item.nextDoseIso,
        "fullfilled_date": calendarDate && calendarDate.toISOString(),
      });
      setSelectedMonth(null);
      setCalendarDate(null);
      updateFulfilledMonthStatus(petData.id, selfSocData.pet[0]);
      reset();
    }
    if (error) {
      userActivityLogger(MIXPANEL_EVENTS.ADD_SELF_REPORT_FAILURE, {
        "pet_name": petData.name,
        "wellness_subscriber": isWellnessPlan ? "yes" : "no",
        "preventative / medication_type": item.name,
        "last_fullfilled": item.lastGivenIso,
        "next_due": item.nextDoseIso,
        "fullfilled_date": calendarDate && calendarDate.toISOString(),
        "failure_reason": error.graphQLErrors[0]?.message,
      });
      reset();
    }
  }, [selfSocData, error]);


  const description = item.details.find((detail: any) => detail.title === "Information")?.description || "";
  const descExpanded = description.length > 200 && !showMore ? `${description.slice(0, 200)}...` : description;


  return (
    <SocChildAccordion expanded={
      isDashboard ? expandedChild === panelId :
        expandedChild === item.name || specificOverdueItem === item.name}
    onChange={handleChangeChild(isDashboard ? panelId : item.name)}
    dynamicStyles={dynamicStyles} >
      <div>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${isDashboard ? panelId: item.name}bh-content`}
          id={`${isDashboard? panelId : item.name}bh-header`}
          onClick={() => onClick(item)}>
          <AccordionHeader>
            <Box className="title_icon w-[65%] !flex">
              {item.status === socStatusMapper.current ?
                <SocCompleteIcon /> : <SocCustomAvatar width={24} height={24} type={item.status} />}
              <Typography variant="body1" className="!capitalize" fontWeight={600}>
                {item.name}
              </Typography>
              {item.name === "Dental Procedure" &&
              <CustomToolTip
                title={
                  <Typography color="inherit">
                            Contact your hospital for {` ${item.name}`}.
                  </Typography>
                }
                placement="top"
              >
                <Box className="text-yellow-500 text-lg font-normal leading-6 cursor-pointer">
                  <InfoIcon width={20} height={20} svgStyle={{ marginLeft: "-0.5rem" }} color="black" />
                </Box>
              </CustomToolTip>}
            </Box>
            <Box>
              {(expandedChild === item.name || specificOverdueItem === item.name &&
                (getSOCStatusDataMap(item.status).label !== "InComplete")) &&
                <ChipCustom
                  icon={<SocStatusCustomIcon type={item?.status as any || "default"}/>}
                  label={getSOCStatusDataMap(item?.status)?.label}
                  className="!font-semibold !text-xs !py-[0.375rem]
                 !px-2 "
                  color={getSOCStatusDataMap(item?.status)?.color}
                  size="small"
                />}

              {specificOverdueItem !== item.name && expandedChild !== item.name &&
                getSOCStatusDataMap(item.status).label !== "InComplete" && (
                <Box className="flex gap-1" color={(theme) => theme.palette.gray.dark}>
                  <Typography variant="body1">
                    {getSOCStatusDataMap(item.status).dueText}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {(item.nextDose === "-" || item.nextDose === "" ) ? "N/A" :
                      formatDateFromUnixTimestamp(+item.nextDose)}
                  </Typography>
                </Box>
              )}
            </Box>
          </AccordionHeader>
        </AccordionSummary>
      </div>

      {isPreventative ?
        <PreventativeMonthCarousal
          fulfilledMonths={item.fullfilledMonths}
          handleSocDataSelect={handleSocDataSelect}
          eventName={item.name} /> :
        null}
      <AccordionDetailsChild>
        {isPreventative ?
          selectedMonth ? (
            <div className="w-full">
              <div className="text-xs font-semibold ps-0.5 mb-1">Fulfilled Date</div>
              <DatePicker
                value={calendarDate}
                onChange={(newValue) => handleDateSelected(newValue)}
                renderInput={(params) => <TextField sx={{ width: "100%", margin: 0 }} {...params} />}
                minDate={minDate}
                disabled={selectedMonth ? false : true}
                disableFuture={true}
                components={{
                  OpenPickerIcon: CalendarIcon,
                }}
              />
              <div className="text-gray-1 font-medium text-xs ps-0.5 mt-1 mb-1.5">
                {`Enter the date ${item.name} was fulfilled`}
              </div>
              {loading ? (
                <CentralLoadingIndicator />
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={calendarDate && selectedMonth && isValidDate ? false : true}
                  fullWidth
                  className="!text-sm !font-semibold !normal-case"
                  onClick={handleSave}
                >
                Save
                </Button>
              )}
            </div>
          ) : null:
          <>
            <Divider className="w-full !mt-3"/>
            <Box className="soc_date" color={(theme)=>theme.palette.gray.dark}>
              <Typography variant="body1">Last Fulfilled:</Typography>
              <Typography variant="body1" fontWeight={600}>
                {(item.lastGiven === "-" || !item.lastGivenIso) ? "N/A" :
                  formatDateFromUnixTimestamp(+item.lastGiven)}
              </Typography>
            </Box>
            <Box className="soc_date" color={(theme)=>theme.palette.gray.dark}>
              <Typography variant="body1">Fulfilled by:</Typography>
              <Typography variant="body1" fontWeight={600}>
                {!item.vetName ? "N/A" : `Dr. ${item.vetName} . GoodVets ${user?.location?.city}`}
              </Typography>
            </Box>

            <Box className={`soc_date ${!descExpanded ? "mb-1": undefined}`}>
              <Typography variant="body1" sx={{
                color: item.status == socStatusMapper.current ? "#262626" :
                  colorTheme[item?.status as keyof typeof colorTheme].color,
              }}>
                Next Due:</Typography>
              <Typography variant="body1" fontWeight={600}
                sx={{
                  color: item.status == socStatusMapper.current ? "#262626" :
                    colorTheme[item?.status as keyof typeof colorTheme].color,
                }}>
                {(item.nextDose === "-" || item.nextDose === "" )? "N/A" :
                  formatDateFromUnixTimestamp(+item.nextDose)}
              </Typography>
            </Box>

            <Divider className="w-full"/>
          </>}
        {descExpanded && (
          <Typography variant="body1" gutterBottom>
            {descExpanded}{" "}
            <span onClick={handleShowMore} className="cursor-pointer font-semibold">
              {description.length > 200 && !showMore ? "Read More" : showMore ? "See Less" : null}
            </span>
          </Typography>
        )}
        {!isPreventative &&
          item.status !== socStatusMapper.current && !isBooking &&
          <Button
            variant="contained"
            id="Book_a_visit_button"
            disabled={isBookingBlocked}
            color="primary"
            fullWidth
            className="!font-semibold !mb-3 !normal-case"
            onClick={handleBookAppointment}>
            Book Appointment </Button>}
      </AccordionDetailsChild>

    </SocChildAccordion>
  );
};

export default SOCChildAccordion;
